import React, { ReactNode, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import useTimemachineState from '../hooks/useTimemachineState';
import { ClipModel } from '@/features/clips/types';
import useClipDownloadMutation from '@/features/clips/hooks/useClipDownloadMutation';
import { Tracking } from '@/shared/services/tracking';

type Props = {
  children: ReactNode;
};

export const TimemachineStateContext = React.createContext(undefined);

// Create a new context for the TimeMachine actions
export type TimemachineActions = {
  downloadClip: (clip: ClipModel) => void;
};

export const TimemachineActionsContext = React.createContext<TimemachineActions | undefined>(
  undefined,
);

/**
 * Helper function to format status message based on current state
 */
//@ts-expect-error
export const formatStatus = (state) => {
  if (!state.status) return '';

  if (state.clip) {
    const clipIdentifier = state.clip.note || `Clip ${state.clip.id}`;
    return `Exporting ${clipIdentifier}: ${state.status}`;
  }

  return `Exporting: ${state.status}`;
};

/**
 * Provider component that manages TimeMachine state and related UI components
 */
const TimeMachineStatusProvider = ({ children }: Props) => {
  const { timemachineState } = useTimemachineState();
  const { enqueueSnackbar } = useSnackbar();
  const downloadClipMutation = useClipDownloadMutation();

  // Function to trigger clip download from any component - directly downloads video
  const downloadClip = useCallback((clip: ClipModel) => {
    downloadClipMutation.mutate({
      clipId: clip.id.toString(),
      video: true,
      source: 'time_machine',
    });
    Tracking.getInstance().track('Download', {
      category: 'Clips',
      type: 'clip',
    });
  }, [downloadClipMutation]);

  // Actions object that will be provided through context
  const timemachineActions: TimemachineActions = {
    downloadClip,
  };

  // Handle snackbar notifications based on clip status updates
  useEffect(() => {
    if (timemachineState.clipStatusUpdate) {
      // TODO: Snackbar notifications commented out pending product owner review
      // TODO: Invalidate the clipGroup
    }
  }, [timemachineState.clipStatusUpdate, enqueueSnackbar]);

  return (
    //@ts-expect-error
    <TimemachineStateContext.Provider value={timemachineState}>
      <TimemachineActionsContext.Provider value={timemachineActions}>
        {children}
      </TimemachineActionsContext.Provider>
    </TimemachineStateContext.Provider>
  );
};

export default TimeMachineStatusProvider;
